import { cn } from "@/lib/utils";

interface AdSenseProps {
  slot?: string;
  size?: "banner" | "rectangle" | "square" | "leaderboard" | "mobile-banner" | "mobile-banner-320x100" | "mobile-banner-320x90";
  className?: string;
  format?: "auto" | "rectangle" | "vertical" | "horizontal";
  responsive?: boolean;
  hideOnMobile?: boolean;
}

const AD_SIZES = {
  banner: { width: 728, height: 90 },
  rectangle: { width: 336, height: 280 },
  square: { width: 250, height: 250 },
  leaderboard: { width: 728, height: 90 },
  "mobile-banner": { width: 320, height: 50 },
  "mobile-banner-320x100": { width: 320, height: 100 },
  "mobile-banner-320x90": { width: 320, height: 90 }
};

export function AdSense({ 
  slot = "placeholder", 
  size = "banner", 
  className,
  format = "auto",
  responsive = true,
  hideOnMobile = false
}: AdSenseProps) {
  const dimensions = AD_SIZES[size];
  
  // For now, show placeholder - replace with real AdSense code when approved
  const isPlaceholder = true; // Set to false when using real ads
  
  if (isPlaceholder) {
    return (
      <a 
        href="https://www.stoneyard.com/?utm_source=wordpop&utm_medium=ad&utm_campaign=bottom_banner" 
        target="_blank" 
        rel="noopener noreferrer"
        aria-label="Advertisement: Stoneyard - Premium Stone and Masonry Supplies"
        className={cn(
          "ad-slot block hover:opacity-95 transition-all duration-200 w-full",
          className
        )}
        style={{
          height: `${dimensions.height}px`,
        }}
      >
        {/* Stoneyard.com Ad Content */}
        {size === "mobile-banner" || size === "mobile-banner-320x90" || size === "mobile-banner-320x100" ? (
          <div 
            className="h-full rounded-lg overflow-hidden flex items-center justify-between px-3 py-2 border transition-all duration-200 hover:shadow-md"
            style={{ 
              backgroundColor: '#f1f5f9',
              borderColor: '#e2e8f0'
            }}
          >
            {/* Left: Logo + Text */}
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 bg-white p-0.5 shadow-sm">
                <img
                  src="/assets/stoneyard-favicon.png"
                  alt="Stoneyard - Premium stone, brick and masonry supplier logo"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col -my-0.5">
                <span className="font-bold text-[13px] leading-tight" style={{ color: '#334155' }}>
                  Stoneyard
                </span>
                <span className="text-[11px] leading-tight" style={{ color: '#64748b' }}>
                  Premium Stone & Masonry
                </span>
              </div>
            </div>
            
            {/* Right: Learn More Link */}
            <div className="flex items-center gap-0.5 text-[11px] font-semibold" style={{ color: '#4a6350' }}>
              <span>Learn more</span>
              <svg 
                className="w-3 h-3" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2.5} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </div>
          </div>
        ) : size === "banner" || size === "leaderboard" ? (
          <div 
            className="h-full rounded-lg shadow-sm hover:shadow-md overflow-hidden flex items-center px-6 py-4 transition-all duration-200"
            style={{ 
              background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
              border: '1px solid #4a6350'
            }}
          >
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-white p-2 shadow-sm">
                <img
                  src="/assets/stoneyard-favicon.png"
                  alt="Stoneyard - Premium stone, brick and masonry supplier logo"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 style={{ color: '#334155' }} className="font-bold text-lg mb-1">Stoneyard</h3>
                <p style={{ color: '#475569' }} className="text-sm">Premium Stone & Masonry</p>
              </div>
            </div>
          </div>
        ) : (
          <div 
            className="h-full rounded-lg shadow-sm hover:shadow-md overflow-hidden p-6 transition-all duration-200"
            style={{ 
              background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
              border: '1px solid #4a6350'
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-white p-2.5 shadow-sm">
                <img
                  src="/assets/stoneyard-favicon.png"
                  alt="Stoneyard - Premium stone, brick and masonry supplier logo"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div>
                <h3 style={{ color: '#334155' }} className="font-bold text-xl">Stoneyard</h3>
                <p style={{ color: '#64748b' }} className="text-xs">stoneyard.com</p>
              </div>
            </div>
            <p style={{ color: '#475569' }} className="text-sm leading-relaxed mb-3">
              Your premier source for premium stone, brick, and masonry supplies. 
              Quality materials for landscaping, patios, walls, and construction projects.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <span 
                className="text-xs px-2 py-1 rounded"
                style={{ backgroundColor: 'rgba(74, 99, 80, 0.2)', color: '#4a6350' }}
              >
                Natural Stone
              </span>
              <span 
                className="text-xs px-2 py-1 rounded"
                style={{ backgroundColor: 'rgba(74, 99, 80, 0.2)', color: '#4a6350' }}
              >
                Pavers
              </span>
              <span 
                className="text-xs px-2 py-1 rounded"
                style={{ backgroundColor: 'rgba(74, 99, 80, 0.2)', color: '#4a6350' }}
              >
                Masonry
              </span>
            </div>
            <div style={{ color: '#4a6350' }} className="text-sm font-semibold">Visit stoneyard.com →</div>
          </div>
        )}
      </a>
    );
  }

  // Real AdSense code (uncomment and configure when approved)
  return (
    <div className={cn("w-full flex justify-center", hideOnMobile && "bottom-ad", className)}>
      <ins 
        className="adsbygoogle"
        style={{
          display: "block",
          width: responsive ? "100%" : `${dimensions.width}px`,
          height: `${dimensions.height}px`
        }}
        data-ad-client="ca-pub-4013124672660725"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive.toString()}
      />
    </div>
  );
}